import {
  fetchBaseQuery,
  FetchBaseQueryError,
  BaseQueryFn,
  FetchArgs,
  retry,
} from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import { SerializedError } from '@reduxjs/toolkit';
import SentryService from '../services/SentryService';

// Create a mutex to prevent multiple token refreshes
const mutex = new Mutex();

// Define the type for the getAccessTokenSilently function
type GetAccessTokenSilentlyFn = (options?: {
  detailedResponse?: boolean;
  authorizationParams?: {
    scope?: string;
    audience?: string;
    [key: string]: any;
  };
  cacheMode?: 'on' | 'off' | 'cache-only';
  timeoutInSeconds?: number;
}) => Promise<string>;

// Helper function to get the Auth0 context outside of React components
let getAccessTokenSilently: GetAccessTokenSilentlyFn | null = null;

export const setAuth0AccessTokenFunction = (tokenFunction: GetAccessTokenSilentlyFn) => {
  getAccessTokenSilently = tokenFunction;
};

/**
 * Get Auth0 access token (for use outside of auth0BaseQuery)
 * Throws if Auth0 is not initialized
 */
export const getAuth0AccessTokenSilently = async (options?: {
  cacheMode?: 'on' | 'off' | 'cache-only';
}) => {
  if (!getAccessTokenSilently) {
    throw new Error('Auth0 not initialized');
  }
  return getAccessTokenSilently(options);
};

/**
 * Check if Auth0 is initialized
 */
export const isAuth0Initialized = () => getAccessTokenSilently !== null;

/**
 * Custom error handling and transformation
 * Converts various error formats to a consistent structure
 */
export interface CustomErrorResponse {
  status: number;
  data: unknown;
  message: string;
}

const handleErrorResponse = (error: FetchBaseQueryError | SerializedError): CustomErrorResponse => {
  // Handle FetchBaseQueryError
  if ('status' in error) {
    // Get error message from the response if possible
    let message = 'Unknown error occurred';
    if (typeof error.data === 'object' && error.data && 'message' in error.data) {
      message = String(error.data.message);
    } else if (typeof error.data === 'string') {
      message = error.data;
    } else if (error.status === 401) {
      message = 'Authentication failed. Please log in again.';
    } else if (error.status === 403) {
      message = 'You do not have permission to perform this action.';
    } else if (error.status === 404) {
      message = 'The requested resource was not found.';
    } else if (typeof error.status === 'number' && error.status >= 500) {
      message = 'Server error. Please try again later.';
    }

    return {
      status: typeof error.status === 'number' ? error.status : 500,
      data: error.data,
      message,
    };
  }

  // Handle SerializedError
  return {
    status: 500,
    data: error,
    message: error.message || 'Unknown error occurred',
  };
};

// Base query with Auth0 token injection and enhanced error handling
const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL || 'https://api.smartscribe.com',
  prepareHeaders: async headers => {
    // Don't set Content-Type header - let fetch/RTK Query handle it automatically
    // This allows FormData to set its own Content-Type with boundary

    if (getAccessTokenSilently) {
      try {
        // Get the token using the provided function
        const token = await getAccessTokenSilently();
        if (token) {
          headers.set('authorization', `Bearer ${token}`);
        }
      } catch (error) {
        // Continue the request without the token - the server will respond with 401 if needed
      }
    }

    return headers;
  },
  // Add timeout to prevent hanging requests
  timeout: 30000, // 30 seconds
});

// Helper function to wait for a specified time
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Track if we're in the initial app load state
let isInitialLoad = true;
// Reset initial load state after a reasonable time (5 seconds)
setTimeout(() => {
  isInitialLoad = false;
}, 5000);

// Enhanced query with token refresh on 401 responses and proper error handling
export const auth0BaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError | SerializedError
> = async (args, api, extraOptions) => {
  // Wait if there's another request currently refreshing the token
  await mutex.waitForUnlock();

  // If we're in initial load state and Auth0 isn't initialized yet, add a small delay
  // to give Auth0 a chance to initialize before making API calls
  if (isInitialLoad && !getAccessTokenSilently) {
    await sleep(500);
    // If Auth0 is still not initialized after delay, we'll proceed anyway
    // and handle the error gracefully
  }

  let result = await baseQuery(args, api, extraOptions);

  // Handle 401 errors by refreshing the token and retrying
  if (result.error && 'status' in result.error && result.error.status === 401) {
    // Check if mutex is locked to prevent multiple refreshes
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        if (getAccessTokenSilently) {
          try {
            // If we're in initial load state and get a 401, add a slightly longer delay
            // to give Auth0 more time to initialize
            if (isInitialLoad) {
              await sleep(1000);
            }

            // Try to get a fresh token, bypassing cache
            await getAccessTokenSilently({ cacheMode: 'off' });

            // Retry the request with the new token
            result = await baseQuery(args, api, extraOptions);
          } catch (refreshError) {
            // Handle token refresh failure
            console.error('Failed to refresh authentication token:', refreshError);

            // If we're in initial load state, this might be due to Auth0 still initializing
            if (isInitialLoad) {
              // Add a slightly longer delay and try one more time
              await sleep(1500);
              try {
                await getAccessTokenSilently({ cacheMode: 'off' });
                // Retry the request with the new token
                result = await baseQuery(args, api, extraOptions);
              } catch (secondError) {
                // If it fails again, continue with the error handling below
                console.error('Second token refresh attempt failed:', secondError);
              }
            }

            // If we're still seeing an error, return the 401
            if (result.error) {
              return {
                error: {
                  status: 401,
                  data: { message: 'Session expired. Please log in again.' },
                },
              };
            }
          }
        }
      } finally {
        // Release the mutex
        release();
      }
    } else {
      // Wait for the mutex to be released by another request,
      // then retry with the fresh token
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }

  // Handle errors with consistent format
  if (result.error) {
    const formattedError = handleErrorResponse(result.error);
    // Log errors in development
    if (process.env.NODE_ENV !== 'production') {
      console.error(`API Error (${formattedError.status}):`, formattedError.message, args);
    }

    // Send error to monitoring service
    SentryService.logEvent(`API Error: ${formattedError.message}`, {
      level: 'error',
      tags: {
        type: 'api_error',
        status: formattedError.status.toString(),
      },
      extra: {
        endpoint: typeof args === 'string' ? args : args.url,
        args: JSON.stringify(args),
        method: typeof args === 'string' ? 'GET' : args.method || 'GET',
        errorStatus: formattedError.status,
        errorMessage: formattedError.message,
        errorData: formattedError.data,
      },
    });

    // Transform the error to our custom format for consistent handling
    return {
      error: {
        status: formattedError.status,
        data: formattedError,
      },
    };
  }

  return result;
};

// Create a retryable version of the baseQuery for endpoints that need automatic retries
export const retryableAuth0BaseQuery = retry(auth0BaseQuery, {
  maxRetries: 3,
});
